import React, { FunctionComponent } from 'react';
import { useLocation } from 'react-router-dom';
import { parse } from 'query-string';
import styled from 'styled-components';
import { FragmentConfiguration } from '@/config';
import { Price as PriceType } from '@/interfaces';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { renderFont } from '@empiriecom/module-components/ThemeProvider';
import { Price } from '@empiriecom/module-components/Price';
import { FormattedMessage } from 'react-intl';

type VoucherPriceProps = {
  /** price used for the voucher computation */
  price: PriceType;
};

const DiscountContainer = styled.div`
  color: ${({ theme: { primary } }) => primary.color.normal};
  font-size: 1.25rem;
  ${({ theme }) => renderFont('base', 'bold', theme)}
  && > div,
  && > div > div {
    display: inline;
    color: ${({ theme: { primary } }) => primary.color.normal};
    ${({ theme }) => renderFont('base', 'bold', theme)}
  }
`;

const SubtextContainer = styled.div`
  color: ${({ theme: { secondary } }) => secondary.color.normal};
  font-size: 0.625rem;
`;

/**
 * This component computes and renders a reduced price with a discount hint for a voucher string extracted from the URL.
 * The relevant URL hash-parameter is `Name` and it must contain `GHRabatt` followed by the reduction value in percent.
 */
export const VoucherPrice: FunctionComponent<VoucherPriceProps> = ({ price }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { hash, search } = useLocation();
  const { Name: NameHash } = parse(hash);
  const Name = NameHash || parse(search)?.Name;
  const { voucherPrice } = useConfig<FragmentConfiguration>();

  if (voucherPrice.enabled && Name && Name.indexOf('GHRabatt') > 0) {
    const [, voucherValue] = (Name as string)?.split('GHRabatt');
    const computedPrice = (price.value * (100 - parseInt(voucherValue, 10))) / 100;
    return (
      <>
        <DiscountContainer data-testid="voucher-price-container">
          <FormattedMessage
            id="voucherprice.discount"
            defaultMessage="{price} mit Ihrem Rabatt"
            values={{ price: <Price price={computedPrice} currency={price.currency} /> }}
          />
        </DiscountContainer>
        <SubtextContainer>
          <FormattedMessage
            id="voucherprice.discount.subtext"
            defaultMessage="Ihr Rabatt wird im Bestellschritt 4 automatisch abgezogen."
          />
        </SubtextContainer>
      </>
    );
  }
  return null;
};

export default VoucherPrice;
